import { useEffect, useRef, useState } from "react";

// A gold-foil scratch overlay. Children (the option card) sit underneath; the
// visitor scratches the foil away with mouse/touch to reveal them. Just 2-3
// strokes reveal the card and its link becomes clickable.
export const ScratchCard = ({ children, hint = "Scratch to reveal", testId, onReveal, className = "rounded-3xl" }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const scratched = useRef(false);
  const tick = useRef(0);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) {
      onReveal && onReveal();
    }
  }, [revealed]);

  useEffect(() => {
    if (revealed) return;
    const c = canvasRef.current;
    const w = wrapRef.current;
    if (!c || !w) return;
    const paint = () => {
      if (scratched.current) return; // never wipe an in-progress scratch
      const rect = w.getBoundingClientRect();
      if (!rect.width) return;
      c.width = rect.width;
      c.height = rect.height;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      const g = ctx.createLinearGradient(0, 0, rect.width, rect.height);
      g.addColorStop(0, "#8a6d1f");
      g.addColorStop(0.5, "#e6c56a");
      g.addColorStop(1, "#9c7a24");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.fillStyle = "rgba(10,16,36,0.82)";
      const fs = Math.max(18, Math.min(28, rect.width * 0.085));
      ctx.font = `800 ${fs}px Outfit, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("✦ " + hint + " ✦", rect.width / 2, rect.height / 2);
    };
    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(w);
    return () => ro.disconnect();
  }, [revealed, hint]);

  const coords = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches?.[0] ?? e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };

  const scratch = (e) => {
    if (!drawing.current || revealed) return;
    scratched.current = true;
    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    const { x, y } = coords(e);
    ctx.globalCompositeOperation = "destination-out";
    // wide, smooth trail so a couple of strokes clear a lot of foil
    if (last.current) {
      ctx.lineWidth = 96;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(x, y, 48, 0, Math.PI * 2);
    ctx.fill();
    last.current = { x, y };
    // check mid-stroke (throttled) so it reveals without needing mouseup
    tick.current += 1;
    if (tick.current % 4 === 0) measure();
  };

  const measure = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let cleared = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 256) {
      total++;
      if (data[i] === 0) cleared++;
    }
    if (total && cleared / total > 0.28) setRevealed(true);
  };

  const start = (e) => {
    drawing.current = true;
    last.current = null;
    scratch(e);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    measure();
  };

  return (
    <div ref={wrapRef} data-testid={testId} className={`relative overflow-hidden ${className}`}>
      {children}
      {!revealed && (
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={scratch}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={scratch}
          onTouchEnd={end}
          onTouchCancel={end}
          className="absolute inset-0 z-10 cursor-pointer"
          style={{ touchAction: "none" }}
          data-testid={testId ? `${testId}-cover` : undefined}
        />
      )}
    </div>
  );
};
